import { DEFAULT_SPEC_ANALYTICS } from '@fullstory/analytics';
import {
  ActionMenu,
  Box,
  Button,
  Fade,
  Flex,
  Pane,
  ReadMoreMarkdown,
  Text,
} from '@fullstory/components';
import { Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';
import { Dashboard, Space } from '@fullstory/proto/fs/services/dx-objects';
import { timestamp } from '@fullstory/proto/google/protobuf';
import { LibraryObjectType } from '@fullstory/routes';
import { noop } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { PRIVATE_SPACE_ID } from '../../constants';
import { SaveAndShareModalContainer } from '../../containers/SaveAndShareModalContainer';
import { useActiveSpaceContext } from '../../contexts/activeSpace';
import { SpaceOwnership } from '../../hooks';
import { StyledAccessDeniedMessage } from '../AccessDeniedMessage';
import { BreadcrumbIndex } from '../BreadcrumbIndex';
import { FGACButton, getFGACButtonExplorerRoleProps } from '../FGACButton';
import { ReturnToSpaceButton } from '../ReturnToSpaceButton';
import { SaveAndShareModal } from '../SaveAndShareModal';
import { SAVE_TO_SPACE_ID, useSpaceObjectModal } from '../SpaceObjectModal';
import { StarToggle } from '../StarToggle';
import { VisibilityLabel } from '../VisibilityLabel';
import { DxObjectTitleCreator } from './DxObjectTitleCreator';
import { RenameModal } from './RenameModal';
import css from './index.module.scss';

export type ResourceExplorerType =
  | 'Funnel'
  | 'Heatmap'
  | 'Journey'
  | 'Metric'
  | 'Retention'
  | 'Dashboard'
  | 'Segment';

export interface FormInterface {
  id: string;
  name: string;
  description: string;
  createdBy: string;
  created: timestamp;
  lastUpdatedBy: string;
  lastUpdated: timestamp;
}

export interface DxObjectHeaderProps {
  ownedBy: string[];
  objectType: LibraryObjectType;
  starred: boolean;
  values?: FormInterface;
  errors: boolean;
  dirty: boolean;
  accessError: boolean;
  isCanned?: boolean;
  returnToDashboard: Dashboard | null;
  returnButton?: React.ReactNode;
  objectSpaces?: SpaceOwnership | null;
  dropdownMenuItems?: React.ComponentPropsWithoutRef<typeof ActionMenu>['actions'];
  children?: React.ReactNode;
  canCreate: boolean;
  canUpdate: boolean;
  visibility?: Visibility;
  renameModalOpen: boolean;
  setRenameModalOpen: (open: boolean) => void;
  modalState: React.ComponentProps<typeof SaveAndShareModal>['action'] | undefined;
  setModalState: (
    action: React.ComponentProps<typeof SaveAndShareModal>['action'] | undefined,
  ) => void;
  saveDisabled: boolean;
  // Save a new object.
  onSave: (payload: {
    name: string;
    dashboard: Dashboard | null;
    space?: Space;
    section?: string;
    visibility?: Visibility;
  }) => void;
  // Save metadata changes.
  onUpdate: (payload: {
    name: string;
    description: string;
    space?: Space;
    section?: string;
    visibility?: Visibility;
  }) => void;
  onDropdownMenuItemsSelect?: React.ComponentPropsWithoutRef<typeof ActionMenu>['onSelect'];
  onValidate: () => boolean;
  // Save form changes to the current object.
  onSubmit: () => void;
  onRevertChanges?: () => void;
  onStarIconClick: (id: string) => void;
  onOwnersClick?: () => void;
  privateDependentObjects: React.ComponentProps<
    typeof SaveAndShareModalContainer
  >['privateDependentObjects'];
}

export const DxObjectHeader = ({
  ownedBy,
  objectType,
  values,
  starred,
  dirty,
  errors,
  accessError,
  isCanned,
  returnToDashboard,
  returnButton,
  objectSpaces,
  dropdownMenuItems,
  children,
  canCreate,
  canUpdate,
  visibility = Visibility.UNSET,
  renameModalOpen,
  setRenameModalOpen,
  modalState,
  setModalState,
  onDropdownMenuItemsSelect,
  onValidate,
  onSubmit,
  onRevertChanges,
  onStarIconClick,
  saveDisabled,
  onSave,
  onUpdate,
  privateDependentObjects,
  onOwnersClick,
}: DxObjectHeaderProps) => {
  const intl = useIntl();
  const { activeSpace } = useActiveSpaceContext();
  const spaceObjectProps = useSpaceObjectModal({
    objectId: values?.id ?? '',
    currentSpaceId: objectSpaces?.space?.id ?? '',
    objectType,
    canTransfer: canUpdate,
    spaceAnalyticsLocation: 'object detail page',
    specAnalytics: DEFAULT_SPEC_ANALYTICS,
    disableOwningSpaceOption: false,
    visibility,
  });
  // Really we just want this to be a prop, but it isn't.
  useEffect(() => spaceObjectProps?.onMenuItemSelected(SAVE_TO_SPACE_ID), [spaceObjectProps]);
  const onSaveModalSubmit = useCallback(
    (
      name: string,
      visibility: Visibility,
      dashboard: Dashboard | null,
      space?: Space,
      section?: string,
    ) => {
      switch (modalState) {
        case 'share':
          onUpdate({
            name: values?.name ?? '',
            description: values?.description ?? '',
            space,
            section,
            visibility,
          });
          break;
        case 'duplicate':
        case 'saveAs':
        case 'save':
          onSave({ name: name ?? values?.name, dashboard, space, section, visibility });
          break;
      }
      setModalState(undefined);
    },
    [modalState, onSave, values?.name, values?.description, setModalState, onUpdate],
  );

  const name = useMemo(
    () =>
      modalState === 'duplicate' || modalState == 'saveAs'
        ? intl.formatMessage(
            {
              defaultMessage: '{name} (copy)',
              id: 'jTogzT',
              description: 'The default name for a new copied resource',
            },
            { name: values?.name },
          )
        : values?.name ?? '',
    [intl, modalState, values?.name],
  );

  const fgacCreateButtonProps = useMemo(
    () => getFGACButtonExplorerRoleProps(canCreate),
    [canCreate],
  );

  const fgacUpdateButtonProps = useMemo(
    () => getFGACButtonExplorerRoleProps(canUpdate),
    [canUpdate],
  );

  const nodeRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <Box testId="dx-object-header">
        <Box marginBottom={values?.id ? 24 : 0} width={1} overflow="hidden">
          <Flex justifyContent="space-between" className={css.noPrint}>
            <Flex justifyContent="flex-start" flexWrap="wrap" gap={8}>
              {!returnButton && <ReturnToSpaceButton objectSpaces={objectSpaces} />}
              {returnButton}
              <BreadcrumbIndex
                objectType={objectType}
                space={
                  visibility === Visibility.PRIVATE ||
                  returnToDashboard?.visibility == Visibility.PRIVATE ||
                  !values?.id
                    ? undefined
                    : objectSpaces?.space
                }
                indexName={values?.id ? values?.name : undefined}
              />
              {!!visibility && (
                <VisibilityLabel
                  visibility={visibility}
                  showLock
                  tooltipContent={
                    values
                      ? intl.formatMessage({
                          defaultMessage: 'This object is only visible to you.',
                          id: 'd6ciTa',
                        })
                      : intl.formatMessage({
                          defaultMessage:
                            'Initially, new objects are set to Private. You can adjust their visibility after saving.',
                          id: 'hTRRC1',
                        })
                  }
                />
              )}
            </Flex>
            {/* Save a new object or update sharing of existing object */}
            <Box display="flex" alignItems="center" gap={8}>
              {!values?.id ? (
                <FGACButton
                  testId="save-btn"
                  variant="fill-primary"
                  disabled={!canCreate || saveDisabled}
                  onClick={() => {
                    if (!onValidate()) return;
                    if (returnToDashboard) {
                      // Don't show save modal if saving to a Dashboard.
                      onSave({ name: name ?? values?.name, dashboard: returnToDashboard });
                    } else {
                      setModalState('save');
                    }
                  }}
                  tooltip={
                    (isCanned || !canCreate) && (
                      <StyledAccessDeniedMessage
                        suggestion="none"
                        accessError={accessError}
                        viewOnly={!canUpdate && !canCreate}
                        isCanned={isCanned}
                      />
                    )
                  }
                  {...fgacCreateButtonProps}
                >
                  <FormattedMessage defaultMessage="Save" id="+itf/D" description="Save button" />
                </FGACButton>
              ) : (
                <Button
                  testId="share-btn"
                  variant="fill-secondary"
                  onClick={() => {
                    setModalState('share');
                  }}
                  disabled={(!canCreate || !canUpdate) && visibility === Visibility.PRIVATE}
                >
                  <FormattedMessage defaultMessage="Share" id="OKhRC6" />
                </Button>
              )}
            </Box>
          </Flex>
        </Box>
        <Flex justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={8}>
          {values?.id && (
            <Flex
              justifyContent="flex-start"
              alignItems="flex-start"
              style={{
                marginRight: '16px',
                maxWidth: 'max(584px, calc(66% - 16px))',
              }}
            >
              <Flex gap={8}>
                <Box className={css.starIcon}>
                  <StarToggle
                    testId="header-star-icon"
                    starred={starred}
                    size={20}
                    onClick={() => onStarIconClick(values?.id)}
                  />
                </Box>
                <Box className={css.title}>
                  <DxObjectTitleCreator
                    title={values.name}
                    ownedBy={ownedBy}
                    description={values.description}
                    canUpdate={canUpdate}
                    createdBy={values.createdBy}
                    created={values.created}
                    lastUpdated={values.lastUpdated}
                    lastUpdatedBy={values.lastUpdatedBy}
                    onRenameClick={payload => {
                      onUpdate({
                        name: payload.name,
                        description: payload.description,
                      });
                    }}
                    objectType={objectType}
                    onOwnersClick={onOwnersClick}
                  />
                </Box>
              </Flex>
            </Flex>
          )}
          <Flex justifyContent="flex-end" alignItems="center" flexWrap="wrap-reverse" gap={16}>
            {children && (
              <Pane borderRight="standard" paddingLeft={16} paddingRight={16}>
                <Flex gap={12}>{children}</Flex>
              </Pane>
            )}
            <Fade in={dirty && !!values?.id && !modalState} nodeRef={nodeRef}>
              <Flex gap={16} justifyContent="flex-end" ref={nodeRef}>
                <Button testId="revert-changes-btn" variant="minimal" onClick={onRevertChanges}>
                  <FormattedMessage
                    defaultMessage="Revert"
                    id="Mfo/hz"
                    description="Revert changes button"
                  />
                </Button>
                {/* Save as */}
                <FGACButton
                  disabled={!canCreate}
                  tooltip={
                    <StyledAccessDeniedMessage
                      suggestion="none"
                      accessError={accessError}
                      viewOnly={!canUpdate && !canCreate}
                      isCanned={isCanned}
                    />
                  }
                  testId="save-as-btn"
                  variant="fill-secondary"
                  onClick={() => {
                    if (onValidate()) {
                      setModalState('saveAs');
                    }
                  }}
                  {...fgacCreateButtonProps}
                >
                  <FormattedMessage
                    defaultMessage="Save As..."
                    id="mlSeOG"
                    description="Save as button"
                  />
                </FGACButton>
                {/* Save changes to this object */}
                <FGACButton
                  testId="save-btn"
                  variant="fill-primary"
                  disabled={isCanned || !canUpdate}
                  onClick={onSubmit}
                  tooltip={
                    <StyledAccessDeniedMessage
                      suggestion="save_as_or_duplicate"
                      accessError={accessError}
                      viewOnly={!canUpdate && !canCreate}
                      isCanned={isCanned}
                    />
                  }
                  {...fgacUpdateButtonProps}
                >
                  <FormattedMessage defaultMessage="Save" id="+itf/D" description="Save button" />
                </FGACButton>
              </Flex>
            </Fade>

            <Flex justifyContent="flex-end" alignItems="center" gap={8} style={{ flex: 1 }}>
              {values?.id && dropdownMenuItems && dropdownMenuItems.length > 0 && (
                <ActionMenu
                  testId="items-menu"
                  iconName="ellipsis-horizontal"
                  actions={dropdownMenuItems}
                  onSelect={onDropdownMenuItemsSelect ?? noop}
                  placement="bottom-end"
                  title={intl.formatMessage({ defaultMessage: 'More actions', id: 'S8/4ZI' })}
                />
              )}
            </Flex>
          </Flex>
        </Flex>
        {/* Only show the description when viewing a saved metric. */}
        {values?.id && values?.description && (
          <Box marginLeft={8} marginTop={24} marginBottom={24} maxWidth={700}>
            <Text
              appearance="bodyMedium"
              color="secondary"
              style={{ overflowWrap: 'break-word' }}
              tag="div"
            >
              <ReadMoreMarkdown
                content={values.description}
                maxLines={3}
                ignoreMultilineComponents
              />
            </Text>
          </Box>
        )}
      </Box>
      {!errors && values && (
        <SaveAndShareModalContainer
          {...spaceObjectProps}
          returnToDashboard={returnToDashboard}
          visibility={
            modalState === 'share'
              ? visibility
              : activeSpace?.space?.id == PRIVATE_SPACE_ID
              ? Visibility.PRIVATE
              : Visibility.PUBLIC
          }
          name={name}
          onCloseSignal={() => setModalState(undefined)}
          open={!!modalState}
          objectType={objectType}
          onSave={onSaveModalSubmit}
          action={modalState ?? 'save'}
          privateDependentObjects={privateDependentObjects}
        />
      )}
      {!errors && values && (
        <RenameModal
          name={values.name}
          onCloseSignal={() => setRenameModalOpen(false)}
          type={objectType}
          description={values.description}
          onSubmit={(name: string, description: string) => {
            onUpdate({
              name,
              description,
            });
            setRenameModalOpen(false);
          }}
          open={renameModalOpen}
        />
      )}
    </>
  );
};
